import CancelIcon from '@mui/icons-material/Cancel'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Logo from 'components/PPLogo'
import { parse } from 'query-string'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation, useNavigate } from 'react-router'
import PromiseFB from 'services/auth/identities/facebook/promisify'
import { createAccount } from 'services/checkout'
import { message } from 'services/snackbar'
import { clearTrialTask } from 'services/users'
import { currentUserSelector } from 'services/users/selectors'
import { getPasswordValidationState, PasswordRequirementState } from 'shared/utils'
import { StoreThunkDispatch } from 'store/state'
import { isEmailValid } from 'utils/email'
import { validateName } from 'utils/name'
import { setAccount } from '../state/actions'
import { SignupContext } from '../state/context'
import { firstValueFrom } from 'rxjs'
import styles from './CreateAccountForm.pcss'

type SecurityCode = { required: boolean, value: string }
const MSG_VERIFY_EMAIL = `We've sent a verification code to your email. Please enter it and click the button below to continue.`
const ERROR_MSG_DEFAULT = 'Something went wrong! Please refresh the page and try again.'

export function CreateAccountForm() {
  const dataLayer = (window as any).dataLayer || [] // Google Tag Manager dataLayer
  const dispatch = useDispatch<StoreThunkDispatch>()
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [{ signupViewConfig }, signupDispatch] = useContext(SignupContext)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [securityCode, setSecurityCode] = useState<SecurityCode>({ required: false, value: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [subscribeEmails, setSubscribeEmails] = useState(true)
  const user = useSelector(currentUserSelector)

  const queryParams = parse(location.search)
  const productHandle = queryParams.plan?.toString() || ''
  const teamHash = queryParams.team_hash?.toString() || ''

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [passwordValidationState, setPasswordValidationState] = useState<Array<PasswordRequirementState>>([])

  const goToLogin = (ott?: string) => {
    navigate(`/login${ott ? '?ott=' + ott : ''}`)
  }

  useEffect(() => {
    // EXPL: Clear trial tasks before signup to avoid caching issues when testing
    dispatch(clearTrialTask())
  }, [dispatch])

  const toggleSubscribeEmails = () => {
    setSubscribeEmails(!subscribeEmails)
  }

  const trackSubmit = (type: 'no_cc_trial' | 'cc_trial') => {
    dataLayer.push({
      'event': 'pp.form_submission',
      'event_parameters': {
        'form': 'create_account',
        'plan': productHandle,
        'trial_type': type
      }
    })
  }

  const reCaptchaVerify = useCallback(() => {
    if ((window as any).Cypress) {
      return Promise.resolve('test-cypress-recaptcha-token')
    }
    if (!executeRecaptcha) {
      dispatch(message('Something went wrong! Please refresh the page and try again.', 'error'))
      return Promise.reject(null)
    }
    return executeRecaptcha('submit')
  }, [executeRecaptcha, dispatch])

  // If user is logged in, redirect to home
  if (user) {
    return <Navigate to="/" />
  }

  const onAccountCreated = (response: any, account: any) => {
    if (response.completed) {
      const data = { ...account, ...response }
      signupDispatch(setAccount(data))
      if (localStorage) {
        localStorage.setItem('pp_signup_account', JSON.stringify(data))
      }
      trackSubmit('no_cc_trial')
      navigate('/signup/verify')
    } else {
      const msg = response.error?.message || ERROR_MSG_DEFAULT
      dispatch(message(msg, 'error'))
      setLoading(false)
    }
  }

  const signupWithFacebook = async () => {
    setLoading(true)
    try {
      const captchaToken = await reCaptchaVerify()
      if (!captchaToken) {
        dispatch(message('ReCaptcha verification failed. Please try again.', 'error'))
        setLoading(false)
        return
      }
      await PromiseFB.login({ scope: ['email', 'public_profile'] })
      const userInfo: any = await PromiseFB.api('/me', { fields: 'first_name, last_name, email, id' })
      const { first_name: fbFirstName, last_name: fbLastName, email, id: fbId } = userInfo
      // Names arriving from Facebook go through the same check as typed ones — the account is
      // created from them either way, so validating only the form would leave the rule half-applied.
      const fName = validateName(fbFirstName || '')
      const lName = validateName(fbLastName || '')
      if (!fName || !lName || !email || !fbId) {
        dispatch(message('We could not get your info from Facebook. Please fill in the form.', 'error'))
        setLoading(false)
        return
      }
      const mail = email.trim()
      const response = await firstValueFrom(dispatch(createAccount({
        firstName: fName,
        lastName: lName,
        email: mail,
        captchaToken,
        fbId,
        securityCode: securityCode.value,
        subscribeEmails
      })))

      if (response.error?.code === 'email-verification-required') {
        setSecurityCode({ required: true, value: '' })
        setLoading(false)
        return
      } if (response.error?.code === 'wrong-security-code') {
        dispatch(message('Wrong security code! Please try again.', 'error'))
        setLoading(false)
        return
      }
      const account = {
        firstName: fName,
        lastName: lName,
        email: mail,
        productHandle
      }
      onAccountCreated(response, account)
      setLoading(false)
    } catch (e) {
      const msg = e?.response?.error?.message || ''
      dispatch(message('An error occurred. Please try again. ' + msg, 'error'))
      setLoading(false)
    }
  }

  const submit = async () => {
    try {
      const captchaToken = await reCaptchaVerify()
      if (!captchaToken) {
        dispatch(message('ReCaptcha verification failed. Please try again.', 'error'))
        return
      }
      const fName = validateName(firstName)
      const lName = validateName(lastName)
      if (!fName) {
        dispatch(message('Please enter a valid first name.', 'error'))
        return
      }
      if (!lName) {
        dispatch(message('Please enter a valid last name.', 'error'))
        return
      }
      // The sanitized value is what gets submitted and stored, so reflect it in the fields rather
      // than leaving the user looking at input that differs from their account.
      setFirstName(fName)
      setLastName(lName)
      if (!isEmailValid(email)) {
        dispatch(message('Please enter a valid email.', 'error'))
        return
      }
      if (!password) {
        dispatch(message('Password field cannot be blank.', 'error'))
        return
      }
      if (!passwordConfirm) {
        dispatch(message('Please confirm your password.', 'error'))
        return
      }
      if (password !== passwordConfirm) {
        dispatch(message('Passwords do not match. Please re-enter your password.', 'error'))
        return
      }
      const validation = getPasswordValidationState(password)
      const allMet = validation.every(r => r.isMet)
      if (!allMet) {
        dispatch(message('Your password must meet the minimum security requirements.', 'error'))
        return
      }

      setLoading(true)
      const mail = email.trim()
      const response = await firstValueFrom(dispatch(createAccount({
        firstName: fName,
        lastName: lName,
        email: mail,
        password,
        captchaToken,
        securityCode: securityCode.value,
        teamHash,
        subscribeEmails
      })))
      if (response.error?.code === 'email-verification-required') {
        setSecurityCode({ required: true, value: '' })
        setLoading(false)
        return
      } if (response.error?.code === 'wrong-security-code') {
        dispatch(message('Wrong security code! Please try again.', 'error'))
        setLoading(false)
        return
      }
      const account = {
        firstName: fName,
        lastName: lName,
        email: mail,
        productHandle
      }
      onAccountCreated(response, account)
    } catch (e) {
      const msg = e?.response?.error?.message || ''
      dispatch(message('An error occurred. Please try again. ' + msg, 'error'))
      setLoading(false)
    }
  }

  const onPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPassword(value)
    setPasswordValidationState(getPasswordValidationState(value))
  }

  const onPasswordConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPasswordConfirm(e.target.value)
  }

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const title = signupViewConfig.titleNoCC || 'Let\'s get your account set up'

  return (
    <div>
      {/* <CreateAccountProgress activeStep="create" /> */}
      <div className={styles.header}>
        <Logo className={styles.logo} key="logo" />
        <h1 className={styles.headline}>Post Planner</h1>
      </div>
      <div className={styles.layout}>
        <h1 className={styles.title}>
          {title}
        </h1>
        <div className={styles.form}>
          <div className={styles.row}>
            <div className={styles['flex-50']}>
              <TextField
                label={signupViewConfig.firstNamePlaceholder || 'First Name'}
                value={firstName}
                variant="outlined"
                name="firstName"
                className={styles.input}
                onChange={(e) => { setFirstName(e.target.value) }}
              />
            </div>
            <div className={styles['flex-50']}>
              <TextField
                value={lastName}
                label={signupViewConfig.lastNamePlaceholder || 'Last Name'}
                variant="outlined"
                name="lastName"
                className={styles.input}
                onChange={(e) => { setLastName(e.target.value) }}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles['flex-100']}>
              <TextField
                value={email}
                type="email"
                variant="outlined"
                label={signupViewConfig.emailPlaceholder || 'Email'}
                className={styles.input}
                onChange={(e) => { setEmail(e.target.value) }}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles['flex-100']}>
              <TextField
                value={password}
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                name="password"
                label="Password"
                className={styles.input}
                onChange={onPasswordChange}
                autoComplete="new-password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword ? 'Hide password' : 'Show password'
                          }
                          onClick={() => { setShowPassword(!showPassword) }}
                          onMouseDown={handleMouseDownPassword}
                          onMouseUp={handleMouseUpPassword}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles['flex-100']}>
              <TextField
                value={passwordConfirm}
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                name="passwordConfirm"
                label="Confirm Password"
                className={styles.input}
                onChange={onPasswordConfirmChange}
                autoComplete="new-password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showConfirmPassword ? 'Hide password' : 'Show password'
                          }
                          onClick={() => { setShowConfirmPassword(!showConfirmPassword) }}
                          onMouseDown={handleMouseDownPassword}
                          onMouseUp={handleMouseUpPassword}
                          edge="end"
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </div>
          </div>
          <div className={styles['flex-100']}>
            <div className={styles.validation}>
              {passwordValidationState.length > 0 && (
                <ul className={styles['pw-requirements']}>
                  <li key="title" className={styles.liTitle}>Password must include:</li>
                  {passwordValidationState.map((r, idx) => (
                    <li key={idx} className={r.isMet ? styles['met'] : styles['not-met']}>
                      {r.isMet ? (
                        <CheckCircleIcon className={`${styles.validationIcon} ${styles.green}`} />
                      ) : (
                        <CancelIcon className={`${styles.validationIcon} ${styles.red}`} />
                      )}
                      {r.requirementShort}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          {securityCode.required && (
            <div className={styles.row}>
              <div className={styles['flex-100']}>
                <div className={styles.warning}>
                  {MSG_VERIFY_EMAIL}
                </div>
                <TextField
                  value={securityCode.value}
                  type="text"
                  name="securityCode"
                  variant="outlined"
                  placeholder="Verification code"
                  className={styles.input}
                  onChange={(e) => { setSecurityCode({ required: true, value: e.target.value }) }}
                />
              </div>
            </div>
          )}
          <div className={styles.actions}>
            <FormControlLabel
              label="Send me weekly social media & AI tips"
              classes={{ label: styles.label }}
              control={(
                <Checkbox
                  checked={subscribeEmails}
                  color="primary"
                  size="small"
                  onChange={toggleSubscribeEmails}
                />
              )}
            />
            <Button
              variant="contained"
              color="primary"
              size="large"
              className={styles['btn-submit']}
              disabled={loading}
              data-testid="btn-create-account"
              onClick={submit}
            >
              {signupViewConfig.mainButtonLabel || 'Create'}
            </Button>
            <p className={`${styles.termsRow} ${styles.label}`}>
              <span>By proceeding, you agree to our </span>
              <a href="https://www.postplanner.com/terms" target="_blank" rel="noreferrer">Terms of Service</a>
            </p>
            <div className={styles.footer}>
              {/* <Button
            variant="contained"
            size="large"
            className={styles['btn-fb']}
            disabled={loading}
            onClick={signupWithFacebook}
          >
            Connect with Facebook
          </Button> */}
              <div className={styles['login-box']}>
                <span>Already have an account?</span>
                <span role="button" className={styles['btn-login']} onClick={() => goToLogin()}>Log in</span>
              </div>
            </div>
          </div>
        </div>
        {/* <div className={styles.divider}>
          <span>OR</span>
        </div> */}
      </div>
    </div>
  )
}
